import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  ExtractorActionsProps,
  ManageReviewersParams,
  Reviewer,
} from '../../ExtractorActions';
import * as styles from './ManageReviewersModal.module.scss';
import { Study } from 'pages/StudyList/StudyList';
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalHeader,
  RadioButton,
  RadioGroup,
  Select,
  Strong,
  Text,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

export type ManageReviewersModalProps = {
  studyId: Study['id'];
  studyTitle: Study['title'];
  primaryReviewer?: ExtractorActionsProps['primaryReviewer'];
  secondaryReviewer?: ExtractorActionsProps['secondaryReviewer'];
  permittedPrimaryReviewers: Reviewer[];
  permittedSecondaryReviewers: Reviewer[];
  isModalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  manageReviewers: ({
    keepPrimaryReviewerData,
    keepSecondaryReviewerData,
    primaryReviewerId,
    secondaryReviewerId,
  }: ManageReviewersParams) => Promise<void>;
  isSingleExtractorMode: boolean;
  showWarningDataForPrimaryReviewer?: boolean;
  showWarningDataForSecondaryReviewer?: boolean;
};

type ManageReviewersFormParams = {
  primaryReviewerId: number | null;
  secondaryReviewerId: number | null;
  primaryReviewerData?: string;
  secondaryReviewerData?: string;
  primaryReviewerConfirmClear?: boolean;
  secondaryReviewerConfirmClear?: boolean;
};

export const ManageReviewersModal = ({
  studyTitle,
  primaryReviewer,
  secondaryReviewer,
  permittedPrimaryReviewers,
  permittedSecondaryReviewers,
  isModalOpen,
  setModalOpen,
  manageReviewers,
  isSingleExtractorMode,
  showWarningDataForPrimaryReviewer,
  showWarningDataForSecondaryReviewer,
}: ManageReviewersModalProps) => {
  const [showPrimaryReviewerWarning, setShowPrimaryReviewerWarning] = useState(
    false
  );
  const [
    showSecondaryReviewerWarning,
    setShowSecondaryReviewerWarning,
  ] = useState(false);
  const [
    isPrimaryReviewerUnassigned,
    setIsPrimaryReviewerUnassigned,
  ] = useState(false);
  const [
    isSecondaryReviewerUnassigned,
    setIsSecondaryReviewerUnassigned,
  ] = useState(false);

  const sameReviewerMessage =
    "The same person can't be the 1st and 2nd reviewer";

  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm<ManageReviewersFormParams>({
    mode: 'onSubmit',
    defaultValues: {
      primaryReviewerId: primaryReviewer?.id ?? null,
      secondaryReviewerId: secondaryReviewer?.id ?? null,
      primaryReviewerData: undefined,
      secondaryReviewerData: undefined,
      primaryReviewerConfirmClear: undefined,
      secondaryReviewerConfirmClear: undefined,
    },
  });

  const validateIsNotSameReviewer = (
    field: 'primaryReviewerId' | 'secondaryReviewerId'
  ) => (value: number | null) => {
    if (isSingleExtractorMode) return true;

    const otherField =
      field === 'primaryReviewerId'
        ? 'secondaryReviewerId'
        : 'primaryReviewerId';

    const otherValue = getValues(otherField);

    if (value == null || otherValue == null) return true;

    return value === otherValue ? sameReviewerMessage : true;
  };

  function handlePrimaryReviewerWarning(
    event: React.ChangeEvent<HTMLSelectElement>
  ): void {
    const raw = event.target.value;
    const nextId = raw === '' ? null : Number(raw);
    const currentId = primaryReviewer?.id ?? null;

    const reviewerChanged = nextId !== currentId;
    const shouldShow = reviewerChanged && !!showWarningDataForPrimaryReviewer;
    setShowPrimaryReviewerWarning(shouldShow);
    setIsPrimaryReviewerUnassigned(nextId === null);

    // Always clear both warning fields on every dropdown change so neither
    // stale value carries over when switching between unassigned and a reviewer.
    setValue('primaryReviewerData', undefined, { shouldDirty: true });
    clearErrors('primaryReviewerData');
    setValue('primaryReviewerConfirmClear', undefined, { shouldDirty: true });
    clearErrors('primaryReviewerConfirmClear');
  }

  function handleSecondaryReviewerWarning(
    event: React.ChangeEvent<HTMLSelectElement>
  ): void {
    const raw = event.target.value;
    const nextId = raw === '' ? null : Number(raw);
    const currentId = secondaryReviewer?.id ?? null;

    const reviewerChanged = nextId !== currentId;
    const shouldShow = reviewerChanged && !!showWarningDataForSecondaryReviewer;
    setShowSecondaryReviewerWarning(shouldShow);
    setIsSecondaryReviewerUnassigned(nextId === null);

    // Always clear both warning fields on every dropdown change so neither
    // stale value carries over when switching between unassigned and a reviewer.
    setValue('secondaryReviewerData', undefined, { shouldDirty: true });
    clearErrors('secondaryReviewerData');
    setValue('secondaryReviewerConfirmClear', undefined, { shouldDirty: true });
    clearErrors('secondaryReviewerConfirmClear');
  }

  const primaryReviewerField = register('primaryReviewerId', {
    setValueAs: (v) => (v === '' ? null : Number(v)),
    validate: validateIsNotSameReviewer('primaryReviewerId'),
  });

  const secondaryReviewerField = register('secondaryReviewerId', {
    setValueAs: (v) => (v === '' ? null : Number(v)),
    validate: validateIsNotSameReviewer('secondaryReviewerId'),
  });

  const primaryReviewerDataField = register('primaryReviewerData', {
    validate: (value) =>
      !showPrimaryReviewerWarning ||
      isPrimaryReviewerUnassigned ||
      !!value ||
      'Please select one option',
  });

  const secondaryReviewerDataField = register('secondaryReviewerData', {
    validate: (value) =>
      !showSecondaryReviewerWarning ||
      isSecondaryReviewerUnassigned ||
      !!value ||
      'Please select one option',
  });

  const primaryReviewerConfirmClearField = register(
    'primaryReviewerConfirmClear',
    {
      validate: (value) =>
        !showPrimaryReviewerWarning ||
        !isPrimaryReviewerUnassigned ||
        value === true ||
        'Please confirm before saving',
    }
  );

  const secondaryReviewerConfirmClearField = register(
    'secondaryReviewerConfirmClear',
    {
      validate: (value) =>
        !showSecondaryReviewerWarning ||
        !isSecondaryReviewerUnassigned ||
        value === true ||
        'Please confirm before saving',
    }
  );

  const onSubmit = async (data: ManageReviewersFormParams) => {
    const keepPrimaryReviewerData =
      showPrimaryReviewerWarning && !isPrimaryReviewerUnassigned
        ? data.primaryReviewerData === 'keep_primary_reviewer_data'
        : null;

    const primaryReviewerId = data.primaryReviewerId;

    const keepSecondaryReviewerData =
      showSecondaryReviewerWarning &&
      !isSingleExtractorMode &&
      !isSecondaryReviewerUnassigned
        ? data.secondaryReviewerData === 'keep_secondary_reviewer_data'
        : null;

    const secondaryReviewerId = !isSingleExtractorMode
      ? data.secondaryReviewerId
      : null;

    await manageReviewers({
      keepPrimaryReviewerData,
      primaryReviewerId: primaryReviewerId !== 0 ? primaryReviewerId : null,
      keepSecondaryReviewerData,
      secondaryReviewerId:
        secondaryReviewerId !== 0 ? secondaryReviewerId : null,
    });
  };

  const primaryHasError = Boolean(errors.primaryReviewerId);
  const secondaryHasError = Boolean(errors.secondaryReviewerId);

  return (
    <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
      <ModalHeader>Manage reviewers</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit(onSubmit)} aria-live="polite">
          <Stack spacing={5}>
            <ModalBody.Heading>{studyTitle}</ModalBody.Heading>
            <div>
              <Stack className={styles.ReviewerContainer}>
                <Inline alignX="spaceBetween">
                  <Stack spacing={1} className={styles.ReviewerSection}>
                    <Text size="sm">
                      <Strong>1st Reviewer</Strong>
                    </Text>
                    <Text size="xs" variant="weak">
                      {`Able to edit this study's extraction forms`}
                    </Text>
                  </Stack>

                  <Stack spacing={1} className={styles.ReviewerSection}>
                    <FormControl>
                      <FormLabel size="sm">Select reviewer</FormLabel>

                      <Select
                        {...primaryReviewerField}
                        size="sm"
                        aria-label="Select primary reviewer"
                        isError={primaryHasError}
                        onChange={(e) => {
                          primaryReviewerField.onChange(e);
                          handlePrimaryReviewerWarning(e);
                          void trigger([
                            'primaryReviewerId',
                            'secondaryReviewerId',
                          ]);
                        }}
                      >
                        <option value="">Unassigned</option>
                        {permittedPrimaryReviewers.map((reviewer) => (
                          <option value={reviewer.id} key={reviewer.id}>
                            {reviewer.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Inline>

                {showPrimaryReviewerWarning && (
                  <Stack
                    className={styles.ExtractionWarningContainer}
                    spacing={1}
                  >
                    <Text size="sm" variant="neutral">
                      <Strong>
                        {primaryReviewer?.name} has started extraction
                      </Strong>
                    </Text>

                    {isPrimaryReviewerUnassigned ? (
                      <>
                        <Text size="sm" variant="neutral">
                          Select a reviewer to preserve their extraction data,
                          otherwise it will be cleared.
                        </Text>
                        <Checkbox
                          {...primaryReviewerConfirmClearField}
                          isError={Boolean(errors.primaryReviewerConfirmClear)}
                          size="sm"
                        >
                          Start new (clear all extraction data)
                        </Checkbox>
                        {errors.primaryReviewerConfirmClear && (
                          <Text variant="danger" size="xs">
                            {errors.primaryReviewerConfirmClear.message}
                          </Text>
                        )}
                      </>
                    ) : (
                      <>
                        <Text size="sm" variant="neutral">
                          Would you like to preserve their extraction data or
                          start new?
                        </Text>

                        <input type="hidden" {...primaryReviewerDataField} />

                        <FormControl
                          error={errors.primaryReviewerData?.message}
                        >
                          <RadioGroup
                            name="primary-reviewer-data-extraction"
                            ariaLabel="Primary reviewer data extraction"
                            layout="Stack"
                            layoutProps={{ spacing: 2, alignX: 'left' }}
                            size="sm"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setValue('primaryReviewerData', e.target.value, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }}
                          >
                            <RadioButton value="keep_primary_reviewer_data">
                              Preserve extraction data
                            </RadioButton>
                            <RadioButton value="start_new_primary_reviewer_data">
                              Start new (clear all extraction data)
                            </RadioButton>
                          </RadioGroup>
                        </FormControl>
                      </>
                    )}
                  </Stack>
                )}
              </Stack>

              {!isSingleExtractorMode && (
                <Stack className={styles.ReviewerContainer}>
                  <Inline alignX="spaceBetween">
                    <div className={styles.ReviewerSection}>
                      <Text size="sm">
                        <Strong>2nd Reviewer</Strong>
                      </Text>
                    </div>

                    <Stack spacing={1} className={styles.ReviewerSection}>
                      <FormControl
                        error={
                          secondaryHasError ? sameReviewerMessage : undefined
                        }
                      >
                        <FormLabel size="sm">Select reviewer</FormLabel>

                        <Select
                          {...secondaryReviewerField}
                          size="sm"
                          aria-label="Select secondary reviewer"
                          isError={secondaryHasError}
                          onChange={(e) => {
                            secondaryReviewerField.onChange(e);
                            handleSecondaryReviewerWarning(e);
                            void trigger([
                              'primaryReviewerId',
                              'secondaryReviewerId',
                            ]);
                          }}
                        >
                          <option value="">Unassigned</option>
                          {permittedSecondaryReviewers.map((reviewer) => (
                            <option value={reviewer.id} key={reviewer.id}>
                              {reviewer.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Inline>

                  {showSecondaryReviewerWarning && (
                    <Stack
                      className={styles.ExtractionWarningContainer}
                      spacing={1}
                    >
                      <Text size="sm" variant="neutral">
                        <Strong>
                          {secondaryReviewer?.name} has started extraction
                        </Strong>
                      </Text>

                      {isSecondaryReviewerUnassigned ? (
                        <>
                          <Text size="sm" variant="neutral">
                            Select a reviewer to preserve their extraction data,
                            otherwise it will be cleared.
                          </Text>
                          <Checkbox
                            {...secondaryReviewerConfirmClearField}
                            isError={Boolean(
                              errors.secondaryReviewerConfirmClear
                            )}
                            size="sm"
                          >
                            Start new (clear all extraction data)
                          </Checkbox>
                          {errors.secondaryReviewerConfirmClear && (
                            <Text variant="danger" size="xs">
                              {errors.secondaryReviewerConfirmClear.message}
                            </Text>
                          )}
                        </>
                      ) : (
                        <>
                          <Text size="sm" variant="neutral">
                            Would you like to preserve their extraction data or
                            start new?
                          </Text>

                          <input
                            type="hidden"
                            {...secondaryReviewerDataField}
                          />

                          <FormControl
                            error={errors.secondaryReviewerData?.message}
                          >
                            <RadioGroup
                              name="secondary-reviewer-data-extraction"
                              ariaLabel="Secondary reviewer data extraction"
                              layout="Stack"
                              layoutProps={{ spacing: 2, alignX: 'left' }}
                              size="sm"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setValue(
                                  'secondaryReviewerData',
                                  e.target.value,
                                  {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  }
                                );
                              }}
                            >
                              <RadioButton value="keep_secondary_reviewer_data">
                                Preserve extraction data
                              </RadioButton>
                              <RadioButton value="start_new_secondary_reviewer_data">
                                Start new (clear all extraction data)
                              </RadioButton>
                            </RadioGroup>
                          </FormControl>
                        </>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}
            </div>

            <Inline alignX="right" spacing={3}>
              <Button
                type="neutral"
                variant="outline"
                htmlType="button"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="brand" htmlType="submit">
                Save
              </Button>
            </Inline>
          </Stack>
        </form>
      </ModalBody>
    </Modal>
  );
};
